import VideoState, {initializeState} from '../video.state';
import {Action, createReducer, on} from '@ngrx/store';
import {
    ChangeSpaceKeyListenerAction,
    GetVideoAction,
    SetImagesPreloadedAction,
    SetInitialVideoValues,
    SetIsCurrentSceneLoaded,
    SetIsInMobilePreviewMode,
    SetIsInPreviewModeAction,
    SetIsIOSFullScreenDisable,
    SetStartSceneTimeAction,
    SetTotalTimeAction,
    SetVideoStateTypeAction,
    SuccessGetVideoAction,
    UpdateCurrentFrameAction,
    UpdateNavigateTimeAction,
    UpdateVideoMasterVolumeAction,
    UpdateForms,
    UpdateVideoTimeAction,
    UpdateForm
} from '../actions/video.action';
import {
    AddResolutionToNotAvailableTable,
    VideoFullScreenStateUpdateAction,
    VideoResolutionMenuVisibilityAction,
    VideoTimelineVisibilityUpdateAction,
    VideoUserResolutionUpdateAction,
    VideoUserVolumeUpdateAction
} from '../actions/timeline.action';
import VideoClipModel from '../../../../../models/video-clip.model';
import {SetAudioTrackIsLoaded, SetAudioTrackIsPlaying} from '../actions/audio.action';
import {ActionFactory} from '../../../../../factories/action.factory';
import SceneModel from '../../../../../models/scene.model';

export const initialState = initializeState();

const reducer = createReducer(
    initialState,
    on(GetVideoAction, state => state),
    on(SuccessGetVideoAction, (state: VideoState, {payload, startState}) => {
        const totalTime = payload.videoClips.reduce((previous, current: VideoClipModel) => previous += current.duration, 0);
        return {
            ...state,
            totalTime,
            videoClips: payload.videoClips,
            videoScenesData: payload.videoScenesData,
            masterVideoVolume: 100,
            audioTracks: payload.audioTracks,
            forms: payload.forms,
            videoError: null,
            currentVideoTime: 0,
            currentVideoFrame: 0,
            nextScene: null,
            actualVideoState: startState,
            actions: payload.actions ? payload.actions.map((actionData) => ActionFactory.CREATE(actionData)) : [],
            project: payload.project,
            isPreviewPurl: payload.project.meta.isPreview,
            preview: state.preview ? state.preview : !!payload.project.meta.isPreview
        };
    }),
    on(UpdateVideoTimeAction, (state: VideoState, {currentTime}) => {
        let currentSceneClip: SceneModel = state.currentSceneClip;
        for (const scene of state.videoScenesData) {
            if (currentTime >= scene.time.start && currentTime < scene.time.end) {
                currentSceneClip = scene;
                break;
            }
        }

        return {
            ...state, currentSceneClip, currentVideoTime: currentTime
        };
    }),
    on(UpdateVideoMasterVolumeAction, (state: VideoState, {currentVolume}) => {
        return {
            ...state, masterVideoVolume: currentVolume
        };
    }),
    on(UpdateCurrentFrameAction, (state: VideoState, {currentFrame}) => {
        return {
            ...state, currentVideoFrame: currentFrame
        };
    }),
    on(SetVideoStateTypeAction, (state: VideoState, {videoState}) => {
        return {
            ...state, actualVideoState: videoState
        };
    }),
    on(VideoUserVolumeUpdateAction, (state: VideoState, {changedUserVolume}) => {
        return {
            ...state, userVolume: changedUserVolume
        };
    }),
    on(VideoFullScreenStateUpdateAction, (state: VideoState, {fullScreenState}) => {
        return {
            ...state, fullScreenState
        };
    }),
    on(UpdateNavigateTimeAction, (state: VideoState, {navigationTime}) => {
        return {
            ...state, navigateToTime: navigationTime
        };
    }),
    on(SetInitialVideoValues, (state: VideoState, {currentScene, overlays, forms}) => {
        return {
            ...state, currentScene, overlays, forms
        };
    }),
    on(SetStartSceneTimeAction, (state: VideoState, {startSceneTime}) => {
        return {
            ...state, startSceneTime
        };
    }),
    on(SetIsCurrentSceneLoaded, (state: VideoState, {isCurrentSceneLoaded}) => {
        return {
            ...state, isCurrentSceneLoaded
        };
    }),
    on(SetAudioTrackIsLoaded, (state: VideoState, {isLoaded, audioTrackId}) => {
        if (isLoaded !== undefined) {
            state.audioTracks.find(audioTrack => audioTrack.id === audioTrackId).loaded = isLoaded;
        }

        return {
            ...state, audioTracks: [...state.audioTracks]
        };
    }),
    on(SetAudioTrackIsPlaying, (state: VideoState, {isPlaying, audioTrackId}) => {
        state.audioTracks.find(audioTrack => audioTrack.id === audioTrackId).isPlaying = isPlaying;

        return {
            ...state, audioTracks: [...state.audioTracks]
        };
    }),
    on(VideoUserResolutionUpdateAction, (state: VideoState, {changedResolution}) => {
        return {
            ...state, userResolution: changedResolution
        };
    }),
    on(VideoTimelineVisibilityUpdateAction, (state: VideoState, {changedVisibility}) => {
        return {
            ...state, timelineVisibility: changedVisibility
        };
    }),
    on(VideoResolutionMenuVisibilityAction, (state: VideoState, {changedVisibility}) => {
        return {
            ...state, resolutionMenuVisibility: changedVisibility
        };
    }),
    on(ChangeSpaceKeyListenerAction, (state: VideoState, {isSpaceKeyListenerOn}) => {
        return {
            ...state, spaceKeyListener: isSpaceKeyListenerOn
        };
    }),
    on(SetIsInMobilePreviewMode, (state: VideoState, {isInMobilePreviewMode}) => {
        return {
            ...state, isInMobilePreviewMode
        };
    }),
    on(SetIsInPreviewModeAction, (state: VideoState, {preview}) => {
        return {
            ...state, preview
        };
    }),
    on(SetImagesPreloadedAction, (state: VideoState, {imagesPreloaded}) => {
        return {
            ...state, imagesPreloaded
        };
    }),
    on(SetTotalTimeAction, (state: VideoState, {totalTime}) => {
        return {
            ...state, totalTime
        };
    }),
    on(AddResolutionToNotAvailableTable, (state: VideoState, {resolution}) => {
        return {
            ...state, notAvailableResolution: [...state.notAvailableResolution, resolution]
        };
    }),
    on(SetIsIOSFullScreenDisable, (state: VideoState, {isIOSFullScreenDisable}) => {
        return {
            ...state, isIOSFullScreenDisable
        };
    }),
    on(UpdateForms, (state: VideoState, {forms}) => {
        return {
            ...state, forms
        };
    }),
    on(UpdateForm, (state: VideoState, {form}) => {
        return {
            ...state,
            forms: state.forms.map(_form => {
                if (_form.id === form.id) {
                    return form;
                }
                return _form
            })
        };
    })
);

export function videoReducer(state: VideoState | undefined, action: Action) {
    return reducer(state, action);
}
